/**
 * useAuth — бутстрап авторизации виджета.
 *
 * Поток:
 *   1) получить контекст виджета (query URL + postMessage PAY_WIDGET_INIT) — widgetContext.ts;
 *   2) обменять signedContext на access-JWT: POST /api/auth/embed
 *      (тело { signedContext, dealId? } — backend EmbedAuthDto);
 *   3) сохранить access-JWT + csrfToken В ПАМЯТИ (apiClient), права → zustand-стор;
 *   4) запланировать авто-refresh за ~60 с до истечения expiresIn (POST /api/auth/refresh).
 *      При провале refresh — тихий re-init через повторный handshake.
 *
 * JWT НЕ хранится в localStorage/cookie (iframe кросс-сайтовый, ITP). При перезагрузке iframe
 * заново проходит handshake — это дёшево.
 *
 * Сверено с backend: auth.controller.ts (embed → { accessToken, tokenType, expiresIn, csrfToken, user }),
 * embed-auth.dto.ts (тело), auth.types.ts (user.permissions).
 */

import { useEffect, useRef, useState } from 'react';
import {
  ApiError,
  clearAuthTokens,
  forceRefreshAccessToken,
  postEmbed,
  setAuthTokens,
} from '../lib/apiClient';
import { useAuthStore } from '../lib/permissions';
import { acquireWidgetContext, type WidgetContext } from '../lib/widgetContext';
import type { EmbedAuthRequest, EmbedAuthResponse } from '../lib/types';

export type AuthPhase = 'handshake' | 'authenticating' | 'ready' | 'error';

export interface UseAuthResult {
  phase: AuthPhase;
  /** Контекст виджета (доступен начиная с фазы authenticating). */
  context: WidgetContext | null;
  /** Ошибка (на фазе error). */
  error: ApiError | Error | null;
  /** Повторить попытку авторизации (например после сетевой ошибки). */
  retry: () => void;
}

/** За сколько секунд до истечения токена запускать проактивный refresh. */
const REFRESH_LEAD_SEC = 60;
/** Минимальная задержка таймера refresh (защита от нулевого/отрицательного expiresIn). */
const MIN_REFRESH_DELAY_SEC = 5;

export function useAuth(): UseAuthResult {
  const [phase, setPhase] = useState<AuthPhase>('handshake');
  const [context, setContext] = useState<WidgetContext | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [attempt, setAttempt] = useState(0);

  const setUser = useAuthStore((s) => s.setUser);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 1) Получаем контекст виджета. Пересоздаётся при retry (attempt).
  useEffect(() => {
    setPhase('handshake');
    const { dispose } = acquireWidgetContext(setContext);
    return dispose;
  }, [attempt]);

  // 2) Как только контекст получен — обмениваем на JWT и планируем refresh.
  useEffect(() => {
    if (!context) return;
    let cancelled = false;

    const clearTimer = (): void => {
      if (refreshTimer.current) {
        clearTimeout(refreshTimer.current);
        refreshTimer.current = null;
      }
    };

    const scheduleRefresh = (expiresInSec: number): void => {
      clearTimer();
      const delaySec = Math.max(MIN_REFRESH_DELAY_SEC, expiresInSec - REFRESH_LEAD_SEC);
      refreshTimer.current = setTimeout(() => {
        void (async () => {
          const ok = await forceRefreshAccessToken();
          if (cancelled) return;
          if (ok) {
            scheduleRefresh(expiresInSec);
          } else {
            // Тихий re-init через повторный handshake (дёшево).
            clearAuthTokens();
            setUser(null);
            setAttempt((n) => n + 1);
          }
        })();
      }, delaySec * 1000);
    };

    const authenticate = async (ctx: WidgetContext): Promise<void> => {
      setPhase('authenticating');
      setError(null);

      // Диагностика: какой контекст получило приложение (читаем в логах сервера, без консоли).
      try {
        const p = new URLSearchParams({
          ev: 'app_ctx',
          acc: String(ctx.accountId ?? ''),
          uid: String(ctx.userId ?? ''),
          sub: String(ctx.subdomain ?? ''),
          deal: String(ctx.dealId ?? ''),
          signed: ctx.signedContext ? '1' : '0',
        });
        const img = new Image();
        img.src = `/api/health/diag?${p.toString()}`;
      } catch {
        /* ignore */
      }

      // Два режима: подписанный контекст (SDK его не выдаёт) ИЛИ незаверенный контекст.
      // accountId необязателен — сервер подставит единственный установленный аккаунт;
      // клиенту достаточно userId.
      if (!ctx.signedContext && !ctx.userId) {
        setError(
          new Error('Не удалось получить userId от amoCRM. Обновите страницу карточки (⌘⇧R).'),
        );
        setPhase('error');
        return;
      }

      const body: EmbedAuthRequest = ctx.signedContext
        ? {
            signedContext: ctx.signedContext,
            ...(ctx.dealId ? { dealId: ctx.dealId } : {}),
          }
        : {
            ...(ctx.accountId ? { accountId: ctx.accountId } : {}),
            ...(ctx.userId ? { userId: ctx.userId } : {}),
            ...(ctx.subdomain ? { subdomain: ctx.subdomain } : {}),
            ...(ctx.dealId ? { dealId: ctx.dealId } : {}),
          };

      try {
        const res = await postEmbed<EmbedAuthResponse>(body);
        if (cancelled) return;
        setAuthTokens({ accessToken: res.accessToken, csrfToken: res.csrfToken });
        setUser(res.user);
        scheduleRefresh(res.expiresIn);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        clearAuthTokens();
        setUser(null);
        setError(e instanceof Error ? e : new Error('Ошибка авторизации'));
        setPhase('error');
      }
    };

    void authenticate(context);

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [context, setUser]);

  return {
    phase,
    context,
    error,
    retry: () => setAttempt((n) => n + 1),
  };
}
